import React, { useMemo, useState } from 'react';
import { toggleSourceFilter } from '../../services/communications/communicationsPresenters.js';
import {
  FilterBar,
  FilterRow,
  FilterLabel,
  FilterDivider,
  SearchWrapper,
  SearchInput,
  FilterPill,
  FilterMeta,
  DropdownWrapper,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from './communicationsPageStyles.js';

const SOURCE_FILTERS = [
  { value: 'outlook', label: 'Outlook' },
  { value: 'teams', label: 'Teams' },
  { value: 'planner', label: 'Planner' },
];

const DATE_FILTERS = [
  { value: '', label: 'Any time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

const ALIGNMENT_FILTERS = [
  { value: '', label: 'Any alignment' },
  { value: 'conflict', label: 'Conflicts only' },
  { value: 'aligned', label: 'Aligned only' },
];

function SimpleDropdown({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <DropdownWrapper onClick={(e) => e.stopPropagation()}>
      <DropdownTrigger data-open={open} onClick={() => setOpen((o) => !o)}>
        <span>{label}: {selected.label}</span>
        <span style={{ color: '#999', fontSize: 10 }}>{open ? '▴' : '▾'}</span>
      </DropdownTrigger>
      <DropdownMenu $open={open}>
        {options.map((opt) => (
          <DropdownItem
            key={opt.value || 'all'}
            $selected={value === opt.value}
            onClick={() => {
              onChange(opt.value);
              setOpen(false);
            }}
          >
            {opt.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </DropdownWrapper>
  );
}

export default function CommunicationsFilterBar({
  filters,
  onFiltersChange,
  members = [],
  resultCount,
  hasConflict,
}) {
  const assigneeOptions = useMemo(
    () => [
      { value: '', label: 'Anyone' },
      ...members.map((m) => ({ value: m.name, label: m.name })),
    ],
    [members]
  );

  const set = (patch) => onFiltersChange({ ...filters, ...patch });
  const selectedSources = filters.sources || [];
  const hasSourceFilter = selectedSources.length > 0;

  const handleSourceToggle = (source) => {
    set({ sources: toggleSourceFilter(selectedSources, source) });
  };

  return (
    <FilterBar>
      <FilterRow>
        <FilterLabel>Filter by</FilterLabel>
        <SearchWrapper>
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
            <circle cx="5.5" cy="5.5" r="4" stroke="#999" strokeWidth="1.5" />
            <path d="M9 9l2.5 2.5" stroke="#999" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <SearchInput
            value={filters.query}
            onChange={(e) => set({ query: e.target.value })}
            placeholder="Search across Outlook, Teams, and Planner…"
          />
        </SearchWrapper>
        <FilterDivider />
        <SimpleDropdown
          label="Assignee"
          value={filters.assignee}
          options={assigneeOptions}
          onChange={(assignee) => set({ assignee })}
        />
        <SimpleDropdown
          label="Date"
          value={filters.dateRange}
          options={DATE_FILTERS}
          onChange={(dateRange) => set({ dateRange })}
        />
        <SimpleDropdown
          label="Alignment"
          value={filters.alignment || ''}
          options={ALIGNMENT_FILTERS}
          onChange={(alignment) => set({ alignment })}
        />
        {(filters.query || hasSourceFilter) ? (
          <FilterMeta $warn={hasConflict}>
            {resultCount} result{resultCount !== 1 ? 's' : ''}
            {hasConflict ? ' · conflict' : ''}
          </FilterMeta>
        ) : null}
      </FilterRow>

      <FilterRow>
        <FilterLabel>Source</FilterLabel>
        <FilterPill
          type="button"
          $active={!hasSourceFilter}
          onClick={() => set({ sources: [] })}
        >
          All
        </FilterPill>
        {SOURCE_FILTERS.map((f) => (
          <FilterPill
            key={f.value}
            type="button"
            $active={selectedSources.includes(f.value)}
            onClick={() => handleSourceToggle(f.value)}
          >
            {f.label}
          </FilterPill>
        ))}
      </FilterRow>
    </FilterBar>
  );
}
